"use client";

import { useEffect, useCallback } from "react";
import { usePathname } from "next/navigation";
import {
  trackEvent,
  trackPageView,
  trackClick,
  trackScrollDepth,
  trackFormFocus,
  trackFormSubmit,
  trackListingView,
  trackListingContact,
  trackSearch,
  trackHeatmapClick,
  initializeAnalyticsSession,
  type TrackEventParams
} from "@/lib/services/analytics.service";
import { EventType } from "@prisma/client";
import { logAnalyticsError } from "@/lib/analytics/analytics-logger";

export function useAnalytics() {
  const pathname = usePathname();

  // Initialize analytics session and track page views
  useEffect(() => {
    // Initialize session on first mount
    initializeAnalyticsSession();

    const startTime = performance.now();

    trackPageView({
      pagePath: pathname || "/",
      pageTitle: document.title,
      loadTime: Math.round(performance.now() - startTime)
    });
  }, [pathname]);

  // Setup scroll tracking
  useEffect(() => {
    trackScrollDepth();
  }, []);

  // Setup global click tracking for heatmaps, rage clicks, and dead clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target) {
        trackHeatmapClick(e.clientX, e.clientY, target);

        // Import these functions dynamically to avoid circular dependencies
        import("@/lib/services/analytics.service").then(({ detectRageClick, detectDeadClick }) => {
          detectRageClick(target);
          detectDeadClick(target);
        });
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Return utility functions
  return {
    trackEvent: useCallback((params: TrackEventParams) => trackEvent(params), []),
    trackClick: useCallback((element: HTMLElement, category?: string) => trackClick(element, category), []),
    trackFormFocus: useCallback((formName: string, fieldName: string) => trackFormFocus(formName, fieldName), []),
    trackFormSubmit: useCallback((formName: string, success: boolean, metadata?: Record<string, any>) =>
      trackFormSubmit(formName, success, metadata), []),
    trackListingView: useCallback((listingId: string, listingPrice?: number, listingType?: string) =>
      trackListingView(listingId, listingPrice, listingType), []),
    trackListingContact: useCallback((listingId: string) => trackListingContact(listingId), []),
    trackSearch: useCallback((searchTerm: string, resultsCount: number, filters?: Record<string, any>) =>
      trackSearch(searchTerm, resultsCount, filters), []),

    // Convenience methods for common events
    trackButtonClick: useCallback((buttonName: string, metadata?: Record<string, any>) => {
      trackEvent({
        eventType: "CLICK" as EventType,
        eventCategory: "button",
        eventAction: "click",
        eventLabel: buttonName,
        metadata
      });
    }, []),

    trackModalOpen: useCallback((modalName: string) => {
      trackEvent({
        eventType: "MODAL_OPEN" as EventType,
        eventCategory: "modal",
        eventAction: "open",
        eventLabel: modalName
      });
    }, []),

    trackModalClose: useCallback((modalName: string) => {
      trackEvent({
        eventType: "MODAL_CLOSE" as EventType,
        eventCategory: "modal",
        eventAction: "close",
        eventLabel: modalName
      });
    }, []),

    trackNavigation: useCallback((from: string, to: string) => {
      trackEvent({
        eventType: "NAVIGATION" as EventType,
        eventCategory: "navigation",
        eventAction: "navigate",
        eventLabel: `${from} -> ${to}`
      });
    }, []),

    trackAuth: useCallback((action: "signin" | "signup" | "signout") => {
      const eventTypes: Record<string, EventType> = {
        signin: "AUTH_SIGNIN" as EventType,
        signup: "AUTH_SIGNUP" as EventType,
        signout: "AUTH_SIGNOUT" as EventType
      };

      const eventType = eventTypes[action];
      if (!eventType) return;

      trackEvent({
        eventType,
        eventCategory: "auth",
        eventAction: action,
        eventLabel: action
      });
    }, []),

    trackError: useCallback((errorMessage: string, errorStack?: string) => {
      trackEvent({
        eventType: "ERROR" as EventType,
        eventCategory: "error",
        eventAction: "error_occurred",
        eventLabel: errorMessage,
        metadata: { stack: errorStack }
      });
    }, [])
  };
}

// Hook for tracking component visibility
export function useTrackVisibility(elementId: string, eventLabel: string) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackEvent({
              eventType: "PAGE_VIEW" as EventType,
              eventCategory: "visibility",
              eventAction: "element_visible",
              eventLabel,
              elementId
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById(elementId);
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [elementId, eventLabel]);
}
